/**
 * Consent management — GDPR + ePrivacy + Google Consent Mode v2.
 *
 * - Default state: everything denied (Consent Mode v2 default script is set
 *   inline in index.html BEFORE any tag loader boots).
 * - User picks a granular state via the banner.
 * - Choices are stored in localStorage with a 12-month expiry
 *   (Garante Privacy IT guidelines).
 * - On update, we push `gtag('consent', 'update', {...})` so that GA4,
 *   Meta Pixel, Google Ads etc. respect the choice from that moment on.
 */

export const CONSENT_KEY = "xlb-consent-v1";
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

/** Category defaults — essenziali always granted, gli altri denied. */
export const DEFAULT_CONSENT = {
  necessary: true,      // sempre on, non modificabile
  statistics: false,    // GA4, misurazione
  marketing: false,     // Meta Pixel, Google Ads, remarketing
};

/** Load the persisted consent (or null if never chosen / expired). */
export function loadConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return { ...DEFAULT_CONSENT, ...parsed.value, __ts: parsed.ts };
  } catch {
    return null;
  }
}

/** Persist consent + notify Google Consent Mode. */
export function saveConsent(partial) {
  const value = { ...DEFAULT_CONSENT, ...partial, necessary: true };
  const record = {
    value,
    ts: Date.now(),
    expiresAt: Date.now() + CONSENT_TTL_MS,
    version: 1,
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    /* ignore */
  }
  pushConsentModeUpdate(value);
  return value;
}

/** Purge consent → next visit shows the banner again. */
export function clearConsent() {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Sync consent with Google Consent Mode v2. If gtag is not loaded yet,
 * the values are queued via dataLayer and picked up when GA/Ads boots.
 */
export function pushConsentModeUpdate(value) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };

  const state = {
    ad_storage: value.marketing ? "granted" : "denied",
    ad_user_data: value.marketing ? "granted" : "denied",
    ad_personalization: value.marketing ? "granted" : "denied",
    analytics_storage: value.statistics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  };

  gtag("consent", "update", state);
}
